import { Box } from "@mui/material";
import { EditorContent, useEditor } from "@tiptap/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { extensions } from "components/Editor/utils";
import { MERGE_TAGS } from "constants/templates";

type EditorMode = "body" | "subject";

// Types for WebView communication
interface WebViewMessage {
  type: string;
  payload?: unknown;
}

interface SetContentPayload {
  html: string;
}

interface ExecuteCommandPayload {
  command: string;
  args?: Record<string, unknown>;
}

type ImageOptionSize = "small" | "medium" | "large" | "full width" | "custom";

// Styles for the editor
const getStyles = (mode: EditorMode) => ({
  container: {
    width: "100%",
    height: mode === "subject" ? "auto" : "100vh",
    minHeight: mode === "subject" ? "22px" : "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
  },
  editor: {
    flex: 1,
    overflow: "auto",
    px: 2,
    mt: mode === "subject" ? -0.25 : 0,
    py: mode === "subject" ? 0 : 1,
    fontSize: 14,
    ".ProseMirror": {
      minHeight: mode === "subject" ? "auto" : "100%",
      outline: "none",
      "&:focus": {
        outline: "none",
      },
    },
    ".ProseMirror p.is-editor-empty:first-of-type::before": {
      color: "#adb5bd",
      content: "attr(data-placeholder)",
      float: "left",
      height: 0,
      pointerEvents: "none",
    },
    ".merge-tag": {
      color: "black",
      backgroundColor: "#fce6df",
      borderRadius: "0.4rem",
      padding: "0.1rem 0.3rem",
      cursor: "pointer",
    },
    ".variable": {
      color: "black",
      backgroundColor: "#fce6df",
      borderRadius: "0.4rem",
      padding: "0.1rem 0.3rem",
      cursor: "pointer",
    },
    table: {
      borderCollapse: "collapse",
      margin: 0,
    },
    "table td, table th": {
      minWidth: "1em",
      boxSizing: "border-box",
      padding: "0",
      position: "relative",
      verticalAlign: "top",
    },
    "table td > *, table th > *": {
      marginBottom: 0,
    },
    "table p": {
      margin: 0,
    },
  },
});

// Helper to post messages to React Native WebView
const postMessageToRN = (type: string, payload?: unknown) => {
  const message = JSON.stringify({ type, payload });

  // React Native WebView injects window.ReactNativeWebView
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(message);
  }

  // Also dispatch a custom event for testing in browser
  window.dispatchEvent(
    new CustomEvent("webviewMessage", { detail: { type, payload } }),
  );
};

// Extend Window interface for React Native WebView
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

export default function WebViewEditor() {
  const [searchParams] = useSearchParams();
  const mode: EditorMode =
    searchParams.get("mode") === "subject" ? "subject" : "body";
  const isInitialized = useRef(false);

  const styles = useMemo(() => getStyles(mode), [mode]);

  const editorExtensions = useMemo(
    () =>
      extensions({
        isPlain: mode === "subject",
        suggestionOptions: MERGE_TAGS,
        placeholderText: mode === "subject" ? "Email Subject" : "Email Body",
        languageToolDisabled: true,
        hideSuggestionDropdown: mode === "subject",
      }),
    [mode],
  );

  const editor = useEditor({
    extensions: editorExtensions,
    content: "",
    onUpdate: ({ editor }) => {
      postMessageToRN("contentChange", {
        html: editor.getHTML(),
        text: editor.getText(),
        isEmpty: editor.isEmpty,
      });
    },
    onSelectionUpdate: ({ editor }) => {
      const { from, to } = editor.state.selection;
      const selectedText = editor.state.doc.textBetween(from, to, " ");
      postMessageToRN("selectionChange", {
        from,
        to,
        isEmpty: editor.state.selection.empty,
        selectedText,
      });
    },
    onFocus: () => {
      postMessageToRN("focus", {});
    },
    onBlur: () => {
      postMessageToRN("blur", {});
    },
  });

  // Handle messages from React Native
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!editor) return;

      let data: WebViewMessage;
      try {
        data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }

      switch (data.type) {
        case "setContent": {
          const payload = data.payload as SetContentPayload;
          if (payload?.html !== undefined) {
            editor.commands.setContent(payload.html);
          }
          break;
        }

        case "getContent": {
          postMessageToRN("content", {
            html: editor.getHTML(),
            text: editor.getText(),
            isEmpty: editor.isEmpty,
          });
          break;
        }

        case "clear": {
          editor.commands.clearContent();
          break;
        }

        case "focus": {
          editor.commands.focus();
          break;
        }

        case "blur": {
          editor.commands.blur();
          break;
        }

        case "insertMergeTag": {
          const tagPayload = data.payload as { tag: string };
          if (tagPayload?.tag) {
            editor
              .chain()
              .focus()
              .insertContentAt(editor.state.selection.anchor, [
                {
                  type: "merge-tag",
                  attrs: { label: tagPayload.tag },
                },
              ])
              .run();
            // Send updated content after inserting merge tag
            postMessageToRN("contentChange", {
              html: editor.getHTML(),
              text: editor.getText(),
              isEmpty: editor.isEmpty,
            });
          }
          break;
        }

        case "executeCommand": {
          const cmdPayload = data.payload as ExecuteCommandPayload;
          if (cmdPayload?.command) {
            const { command, args } = cmdPayload;
            switch (command) {
              case "toggleBold":
                editor.chain().focus().toggleBold().run();
                break;
              case "toggleItalic":
                editor.chain().focus().toggleItalic().run();
                break;
              case "toggleStrike":
                editor.chain().focus().toggleStrike().run();
                break;
              case "toggleCode":
                editor.chain().focus().toggleCode().run();
                break;
              case "toggleBulletList":
                editor.chain().focus().toggleBulletList().run();
                break;
              case "toggleOrderedList":
                editor.chain().focus().toggleOrderedList().run();
                break;
              case "setTextAlign":
                if (args?.align) {
                  editor
                    .chain()
                    .focus()
                    .setTextAlign(args.align as string)
                    .run();
                }
                break;
              case "setColor":
                if (args?.color) {
                  editor
                    .chain()
                    .focus()
                    .setColor(args.color as string)
                    .run();
                }
                break;
              case "setLink":
                if (args?.href) {
                  editor
                    .chain()
                    .focus()
                    .setLink({ href: args.href as string })
                    .run();
                }
                break;
              case "unsetLink":
                editor.chain().focus().unsetLink().run();
                break;
              case "setHeading":
                if (args?.level) {
                  editor
                    .chain()
                    .focus()
                    .toggleHeading({
                      level: args.level as 1 | 2 | 3 | 4 | 5 | 6,
                    })
                    .run();
                }
                break;
              case "undo":
                editor.chain().focus().undo().run();
                break;
              case "redo":
                editor.chain().focus().redo().run();
                break;
              case "insertImage":
                if (args?.src) {
                  editor
                    .chain()
                    .focus()
                    .setImage({
                      src: args.src as string,
                      size: (args.size as ImageOptionSize) || "medium",
                    })
                    .run();
                }
                break;
              case "insertContent":
                if (args?.content) {
                  editor
                    .chain()
                    .focus()
                    .insertContent(args.content as string)
                    .run();
                }
                break;
              default:
                console.warn("Unknown command:", command);
            }
            // Send updated state after command execution
            postMessageToRN("commandExecuted", {
              command,
              state: getEditorState(editor),
            });
          }
          break;
        }

        case "getState": {
          postMessageToRN("state", getEditorState(editor));
          break;
        }

        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor],
  );

  // Get current editor state for formatting buttons
  const getEditorState = (editorInstance: typeof editor) => {
    if (!editorInstance) return { mode };

    // Get selected text
    const { from, to } = editorInstance.state.selection;
    const selectedText = editorInstance.state.doc.textBetween(from, to, " ");

    // For subject mode, return simplified state (no formatting available)
    if (mode === "subject") {
      return {
        mode,
        selectedText,
        canUndo: editorInstance.can().undo(),
        canRedo: editorInstance.can().redo(),
      };
    }

    // Get link href if cursor is in a link
    let linkHref: string | undefined;
    const isLink = editorInstance.isActive("link");
    if (isLink) {
      const linkMark = editorInstance.getAttributes("link");
      linkHref = linkMark?.href;
    }

    return {
      mode,
      isBold: editorInstance.isActive("bold"),
      isItalic: editorInstance.isActive("italic"),
      isStrike: editorInstance.isActive("strike"),
      isCode: editorInstance.isActive("code"),
      isBulletList: editorInstance.isActive("bulletList"),
      isOrderedList: editorInstance.isActive("orderedList"),
      isLink,
      linkHref,
      selectedText,
      textAlign: editorInstance.isActive({ textAlign: "left" })
        ? "left"
        : editorInstance.isActive({ textAlign: "center" })
          ? "center"
          : editorInstance.isActive({ textAlign: "right" })
            ? "right"
            : "left",
      headingLevel: editorInstance.isActive("heading", { level: 1 })
        ? 1
        : editorInstance.isActive("heading", { level: 2 })
          ? 2
          : editorInstance.isActive("heading", { level: 3 })
            ? 3
            : 0,
      canUndo: editorInstance.can().undo(),
      canRedo: editorInstance.can().redo(),
    };
  };

  // Set up message listener
  useEffect(() => {
    // Listen for messages from React Native
    window.addEventListener("message", handleMessage);

    // Also listen for custom events (for testing)
    const handleCustomEvent = (e: Event) => {
      const customEvent = e as CustomEvent;
      handleMessage({ data: customEvent.detail } as MessageEvent);
    };
    window.addEventListener("rnMessage", handleCustomEvent);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("rnMessage", handleCustomEvent);
    };
  }, [handleMessage]);

  // Notify React Native when editor is ready
  useEffect(() => {
    if (editor && !isInitialized.current) {
      isInitialized.current = true;
      postMessageToRN("ready", {
        mode,
        availableMergeTags: MERGE_TAGS,
      });
    }
  }, [editor, mode]);

  if (!editor) {
    return null;
  }

  const handleContainerClick = () => {
    if (editor && !editor.isFocused) {
      editor.commands.focus("end");
    }
  };

  return (
    <Box sx={styles.container} onClick={handleContainerClick}>
      <Box sx={styles.editor}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
